import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import BoqGrid from "@/components/v2/BoqGrid";
import { v2Api } from "@/lib/v2Api";

/**
 * RfpBoqTab — editable Bill of Quantities for one RFP.
 *
 * The BOQ is backed by a Bid record internally, but that's an implementation
 * detail — this component surfaces only BOQ concepts to the user.
 */
export default function RfpBoqTab({ rfp }) {
  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialising, setInitialising] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const projectId = rfp?.project_id;
  const rfpId = rfp?.id;

  const loadBid = useCallback(async () => {
    if (!projectId || !rfpId) return;
    setLoading(true);
    setError(null);
    try {
      const bids = await v2Api.projects.bids(projectId);
      const match = (Array.isArray(bids) ? bids : []).find(
        (b) => String(b.rfp_id) === String(rfpId),
      );
      setBid(match || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load BOQ");
    } finally {
      setLoading(false);
    }
  }, [projectId, rfpId]);

  useEffect(() => { loadBid(); }, [loadBid]);

  // Auto-sync from RFP when BOQ has no lines yet.
  useEffect(() => {
    if (!bid?.id) return;
    setSyncError(null);
    v2Api.boq.list(bid.id).then((lines) => {
      if (!lines || lines.length === 0) {
        setSyncing(true);
        v2Api.boq.syncFromRfp(bid.id)
          .then((synced) => {
            toast.success(`${synced?.length || 0} items loaded from RFP analysis`);
            setRefreshKey((k) => k + 1);
          })
          .catch((e) => {
            const msg = e?.response?.data?.detail || e?.message || "Could not fetch from RFP";
            setSyncError(msg);
          })
          .finally(() => setSyncing(false));
      }
    }).catch(() => {});
  }, [bid?.id]);

  // Initialise BOQ for the first time (creates the backing record + fetches from RFP).
  const initialise = async () => {
    setInitialising(true);
    setError(null);
    try {
      const created = await v2Api.bids.create({
        project_id: projectId,
        rfp_id: rfpId,
        name: (rfp?.title || rfp?.name || "BOQ").slice(0, 240),
        submitted_value_currency: "INR",
      });
      setBid(created);
      // auto-sync fires via the useEffect above once bid?.id is set
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : (e?.message || "Could not initialise BOQ"));
      setError(msg);
      toast.error(msg);
    } finally {
      setInitialising(false);
    }
  };

  const syncFromRfp = async () => {
    if (!bid?.id) return;
    setSyncing(true);
    setSyncError(null);
    try {
      const lines = await v2Api.boq.syncFromRfp(bid.id);
      toast.success(`Synced ${lines?.length || 0} items from RFP analysis`);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Sync failed";
      setSyncError(msg);
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  if (!projectId || !rfpId) {
    return (
      <div className="p-8 text-sm text-slate-400">
        BOQ is available once this RFP is linked to a project.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading BOQ…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 space-y-2">
        <p className="text-sm text-rose-700">{error}</p>
        <Button size="sm" variant="outline" onClick={loadBid}>Retry</Button>
      </div>
    );
  }

  if (!bid) {
    return (
      <div className="p-8 max-w-lg space-y-3">
        <p className="text-sm text-slate-500">
          No BOQ yet. Fetch items from the RFP analysis to get started —
          you can edit, add manual items, or match catalogue entries after.
        </p>
        <Button onClick={initialise} disabled={initialising}>
          {initialising
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Fetching from RFP…</>
            : "Fetch from RFP"}
        </Button>
      </div>
    );
  }

  if (syncing) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Fetching AI-detected items from RFP…
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        {syncError ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex-1 mr-3">
            {syncError} — add items manually below or retry sync.
          </p>
        ) : <span />}
        <Button size="sm" variant="outline" disabled={syncing} onClick={syncFromRfp}>
          {syncing ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Syncing…</> : "Re-sync from RFP"}
        </Button>
      </div>
      <BoqGrid key={refreshKey} bid={bid} project={{ id: projectId }} refreshBid={loadBid} rfpId={rfpId} mode="boq" />
    </div>
  );
}
